import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { confirm, haptic } from '../lib/telegram';
import AmountInput from '../components/AmountInput';
import { formatAmount, parseAmountInput } from '../lib/format';
import type { Budget, Currency } from '../lib/types';

interface Props { onBack: () => void; }

function barColor(ratio: number): string {
  if (ratio >= 1) return '#c0392b';
  if (ratio >= 0.8) return '#f59e0b';
  return '#2e8b57';
}

export default function Budgets({ onBack }: Props) {
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [category, setCategory] = useState('');
  const [currency, setCurrency] = useState<Currency>('SYP');
  const [limit, setLimit] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  async function load() {
    setErr(null);
    try {
      const [b, c] = await Promise.all([api.listBudgets(), api.listCategories()]);
      setBudgets(b.budgets);
      setCategories(c.categories);
      setCategory((prev) => prev || c.categories[0] || '');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed to load');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    const lim = parseAmountInput(limit);
    if (!category) { setErr('Pick a category'); return; }
    if (lim === null) { setErr('Limit must be a positive number'); return; }
    setErr(null); setBusy(true);
    try {
      await api.putBudget(category, currency, lim);
      setLimit('');
      haptic('success');
      await load();
    } catch (e) {
      haptic('error');
      setErr(e instanceof Error ? e.message : 'failed to save');
    } finally { setBusy(false); }
  }

  async function remove(b: Budget) {
    if (!(await confirm(`Remove the ${b.category} budget?`))) return;
    try {
      await api.removeBudget(b.category, b.currency);
      haptic('success');
      await load();
    } catch (e) {
      haptic('error');
      setErr(e instanceof Error ? e.message : 'failed');
    }
  }

  return (
    <>
      <div className="header">
        <button className="back" onClick={onBack}>← Back</button>
        <h1>Budgets</h1>
      </div>
      {err && <div className="error">{err}</div>}

      <form className="card" onSubmit={save}>
        <div className="section-title">Set a monthly budget</div>
        <label className="label" style={{ marginTop: 0 }}>Category</label>
        <select className="select" value={category} onChange={(e) => setCategory(e.target.value)}>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>

        <label className="label">Currency</label>
        <div className="segmented">
          <button type="button" className={currency === 'SYP' ? 'active' : ''} onClick={() => setCurrency('SYP')}>SYP</button>
          <button type="button" className={currency === 'USD' ? 'active' : ''} onClick={() => setCurrency('USD')}>USD</button>
        </div>

        <label className="label">Monthly limit</label>
        <AmountInput value={limit} onChange={setLimit} currency={currency} />

        <button type="submit" className="btn" style={{ marginTop: 12 }} disabled={busy}>
          {busy ? 'Saving…' : 'Save budget'}
        </button>
        <p className="muted small" style={{ marginBottom: 0 }}>
          The bot warns you at 80% and again when you go over.
        </p>
      </form>

      <div className="card">
        <div className="section-title">Your budgets</div>
        {loading ? <div className="muted">Loading…</div> : budgets.length === 0 ? (
          <div className="empty">No budgets yet — add one above.</div>
        ) : budgets.map((b) => {
          const spent = b.spent_this_month ?? 0;
          const lim = Number(b.monthly_limit);
          const ratio = lim > 0 ? spent / lim : 0;
          return (
            <div className="cat-row" key={`${b.category}|${b.currency}`}>
              <div className="cat-head">
                <span className="cat-name">{b.category} · {b.currency}</span>
                <span>
                  <span className={ratio >= 1 ? 'amount-neg' : ''} style={{ fontWeight: 700 }}>
                    {formatAmount(spent, b.currency)}
                  </span>
                  <span className="muted small"> / {formatAmount(lim, b.currency)}</span>
                  <button
                    onClick={() => remove(b)}
                    style={{ background: 'transparent', border: 'none', color: 'var(--destructive)', cursor: 'pointer', marginLeft: 8 }}
                  >✕</button>
                </span>
              </div>
              <div className="cat-bar">
                <div className="cat-bar-fill" style={{ width: `${Math.min(ratio * 100, 100)}%`, background: barColor(ratio) }} />
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
